import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  combineLatest,
  fromEvent,
  Subject,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';

import { StateManagerProjectService } from '@state-manager/state-manager-project.service';
import { CornerstoneService } from '@viewer/cornerstone.service';
import {
  HotKey,
  HotKeySettings,
  ImageSliderComponentKeyActionName,
  ImageSliderComponentKeyActionNameList,
  ViewWindowIndex,
} from '@viewer/data';
import { KeyInteractionService } from 'src/app/interaction';

@Component({
  selector: 'app-image-slider',
  templateUrl: 'image-slider.component.html',
  styleUrls: ['image-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageSliderComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() useHotKeys = true;

  @ViewChild('sliceSlider', { read: ElementRef, static: false })
  sliceSlider: ElementRef<any> | null = null;
  @ViewChild('inputPrevious', { read: ElementRef, static: false })
  inputPrevious: ElementRef<any> | null = null;
  @ViewChild('inputNext', { read: ElementRef, static: false })
  inputNext: ElementRef<any> | null = null;

  totalNumberOfSlices: number = 0;
  currentNumberOfSlice: number = 0;

  private _unsubscribe$ = new Subject();
  private _controlCloseEvent$: Subject<void>;

  constructor(
    public stateManagerProjectService: StateManagerProjectService,
    public cornerstoneService: CornerstoneService,
    private _cdr: ChangeDetectorRef,
    private _keyInteractionService: KeyInteractionService
  ) {
    combineLatest([
      this.stateManagerProjectService.currentSelectedWindow$,
      this.stateManagerProjectService.currentActiveSliceIndex$,
      this.stateManagerProjectService.totalNumberOfSlices$,
    ])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(
        ([
          currentSelectedWindow,
          currentActiveSliceIndex,
          totalNumberOfSlices,
        ]) => {
          this.totalNumberOfSlices =
            currentSelectedWindow === 0 || currentSelectedWindow === 4
              ? 0
              : totalNumberOfSlices[`viewer${currentSelectedWindow}`];

          this.currentNumberOfSlice =
            currentSelectedWindow === 0 || currentSelectedWindow === 4
              ? 0
              : currentActiveSliceIndex[`viewer${currentSelectedWindow}`];

          this._cdr.markForCheck();
        }
      );

    this._controlCloseEvent$ = new Subject<void>();

    window.addEventListener('resize', () => {
      this._cdr.markForCheck();
    });
  }

  ngOnInit(): void {
    if (this.useHotKeys) {
      this._initializeHotKeys();
    }
  }

  public ngAfterViewInit(): void {
    this._subscriptionForHoldButtons();

    const viewerElements = this.cornerstoneService.getViewerElements();
    Array.from(Array(3)).forEach((_, index) => {
      const i: ViewWindowIndex = index + 1;
      const element = viewerElements[i];
      fromEvent(element, 'cornerstonenewimage')
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe(() => setTimeout(() => this._cdr.markForCheck(), 50));
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next(undefined);
    this._unsubscribe$.complete();

    if (this.useHotKeys) {
      this._keyInteractionService.removeKeySettingsList([
        ...ImageSliderComponentKeyActionNameList,
      ]);
    }
  }

  private _subscriptionForHoldButtons(): void {
    const mouseDownPreviousKey$ = fromEvent(
      this.inputPrevious!.nativeElement,
      'mousedown'
    );
    const mouseDownNextKey$ = fromEvent(
      this.inputNext!.nativeElement,
      'mousedown'
    );

    const mouseUpEvent$ = fromEvent(document, 'mouseup');

    const streamPrevious$ = mouseDownPreviousKey$.pipe(
      switchMap(() => timer(300, 30).pipe(takeUntil(mouseUpEvent$)))
    );
    const streamNext$ = mouseDownNextKey$.pipe(
      switchMap(() => timer(300, 30).pipe(takeUntil(mouseUpEvent$)))
    );

    streamPrevious$
      .pipe(takeUntil(this._controlCloseEvent$))
      .subscribe(() => this.changeSlice(false));
    streamNext$
      .pipe(takeUntil(this._controlCloseEvent$))
      .subscribe(() => this.changeSlice(true));
  }

  public percentsOfViewed(): string {
    const map = this.cornerstoneService.getMapOfViewedSlicesForCurrentWindow();
    if (!map) {
      return '';
    }

    const percents = Math.trunc(
      (map.filter((el) => el === true).length / map.length) * 100
    );

    return Number.isNaN(percents) ? '' : `${percents}%`;
  }

  public changeSlice(value: boolean): void {
    if (
      (value &&
        this.currentNumberOfSlice + 1 >
          this.stateManagerProjectService.totalNumberOfSlices[
            `viewer${this.stateManagerProjectService.currentSelectedWindow}`
          ]) ||
      (!value && this.currentNumberOfSlice - 1 < 0)
    ) {
      return;
    }

    this.cornerstoneService.setIndexOfSlider(
      value ? this.currentNumberOfSlice + 1 : this.currentNumberOfSlice - 1,
      this.stateManagerProjectService.currentSelectedWindow
    );
  }

  public changeCurrentSlice(event: any): void {
    let value = parseInt(event.target.value);
    this.cornerstoneService.setIndexOfSlider(
      value,
      this.stateManagerProjectService.currentSelectedWindow
    );
  }

  public getTotalSlicesInfo(): number {
    return [ViewWindowIndex.Empty, ViewWindowIndex.Model3D].includes(
      this.stateManagerProjectService.currentSelectedWindow
    )
      ? 1
      : this.stateManagerProjectService.totalNumberOfSlices[
          `viewer${this.stateManagerProjectService.currentSelectedWindow}`
        ] + 1;
  }

  public calculateWidthOfSlices(): string {
    const elems = document.getElementsByClassName('not-viewed-slice');
    let width: number = 0;
    for (let i = 0; i < elems.length; i++) {
      width = width + elems[i].getBoundingClientRect().width;
    }

    return width ? width + 0 + 'px' : '100%';
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.ArrowLeft,
        new HotKeySettings<ImageSliderComponentKeyActionName>({
          keyActionName: 'PreviousSliceTrigger',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.changeSlice(false);
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.ArrowRight,
        new HotKeySettings<ImageSliderComponentKeyActionName>({
          keyActionName: 'NextSliceTrigger',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.changeSlice(true);
      });
  }
}
